import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AbstractControl, FormControl, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import * as ExcelJS from 'exceljs';

import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { ToolbarModule } from 'primeng/toolbar';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { FileUpload, FileUploadHandlerEvent, FileUploadModule } from 'primeng/fileupload';
import { CustomCreateButton, CustomTitle, ISelectOption } from 'lib-base';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-options-builder',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule, DialogModule, ButtonModule, TableModule, TranslateModule,
    ToolbarModule, DropdownModule, InputTextModule, TooltipModule, ToastModule,
    FileUploadModule,
    HttpClientModule,
    CustomCreateButton, CustomTitle
  ],
  providers: [MessageService],
  templateUrl: './options-builder.component.html',
  styleUrls: ['./options-builder.component.scss']
})
export class OptionsBuilderComponent {
  @Input() header !: string;
  @Input() originalOptions: ISelectOption[];
  @Output() onApplyOptionsEvent = new EventEmitter<ISelectOption[]>();

  displayModal: boolean = false;
  cols: any[] = [];
  deletableCols: any[] = [];
  idCounter: number = 0;
  editableOptions: ISelectOption[] = [];
  clonedOptions: { [s: string]: ISelectOption; } = {};

  addInput !: FormControl;
  deleteDropdown !: FormControl;

  readonly COLUM_NAME_PATTERN = /^\S*$/;
  constructor(private messageService: MessageService) {
    this.header = 'Control';
    this.originalOptions = [];
    this.addInput = new FormControl('', [
      Validators.required,
      Validators.pattern(this.COLUM_NAME_PATTERN),
      this.uniqueColumnNameValidator()
    ])
    this.deleteDropdown = new FormControl('');
  }

  openModal() {
    this.displayModal = true;
    this.initializeColumns(this.originalOptions);
  }

  initializeColumns(options: ISelectOption[]) {
    if (options.length === 0) return;
    this.idCounter = 0;
    //generate a unique id for each option, this for editing purposes
    this.editableOptions = options.map((option, index) => {
      this.idCounter++;
      return { ...option, id: this.idCounter.toString() };
    });
    //get the visible columns
    this.cols = Object.keys(this.editableOptions[0]).filter(item => item !== "id");
    this.setDeletableCols();
  }

  setDeletableCols() {
    this.deletableCols = this.cols.filter(item => item !== "value" && item !== "text" && item !== "id");
  }

  addColumn() {
    if (this.addInput.invalid) return;
    if (this.cols.includes(this.addInput.value)) return;

    this.cols.push(this.addInput.value);

    this.editableOptions.forEach(option => {
      option[this.addInput.value] = "";
    });

    this.setDeletableCols();
    this.addInput.reset();
  }

  deleteColumn() {
    if (!this.deleteDropdown.value && this.deleteDropdown.value !== "") return;

    this.cols = this.cols.filter(item => item !== this.deleteDropdown.value);
    this.editableOptions.forEach(option => {
      delete option[this.deleteDropdown.value];
    });
    this.setDeletableCols();
    this.deleteDropdown.reset();
  }

  generateUniqueValue(): string {
    let i = 1;
    //check if the control name already exists
    while (this.editableOptions.find(option => option.value === i.toString()) != null) {
      i++;
    }
    return i.toString();
  }

  addRow() {
    this.idCounter++;
    const newValue = this.generateUniqueValue();
    let newRow: ISelectOption = {
      id: this.idCounter.toString(),
      value: newValue,
      text: 'Option ' + newValue
    };
    this.cols.forEach(col => {
      if (!(col in newRow))
        newRow[col] = "";
    });
    this.editableOptions = [...this.editableOptions, newRow];
  }

  onRowEditInit(option: ISelectOption) {
    if (option["id"] !== undefined)
      this.clonedOptions[option["id"]] = { ...option };
  }

  onDeleteRow(index: number) {
    let updatedOptions = [...this.editableOptions];
    updatedOptions.splice(index, 1);
    this.editableOptions = updatedOptions;
  }

  onRowEditSave(option: ISelectOption) {
    if (option["id"] === undefined) return;

    if (!this.allfieldsHaveValues(option)) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'All fields must have values' });
      return;
    }

    if (!this.hasUniqueValue(option["value"])) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Value must be unique' });
      return;
    }

    delete this.clonedOptions[option["id"]];
    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'option is updated' });
  }

  onRowEditCancel(option: ISelectOption, index: number) {
    if (option["id"] !== undefined) {
      this.editableOptions[index] = this.clonedOptions[option["id"]];
      delete this.clonedOptions[option["id"]];
    }
  }

  applyOptions() {
    //check all editable options and see if they all have values and if the value is unique
    const isInvalid = this.editableOptions.some(option => {
      if (!this.hasUniqueValue(option["value"])) {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'All the values must be unique' });
        return true;
      }

      if (!this.allfieldsHaveValues(option)) {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'All fields must have values' });
        return true;
      }

      return false;
    });

    if (!isInvalid) {
      this.displayModal = false;
      //we dont need the id column
      const newOptions = this.editableOptions = this.editableOptions.map(option => {
        const { id, ...rest } = option;
        return rest;
      });
      this.onApplyOptionsEvent.emit(newOptions);
    }
  }

  //#region  Validation
  hasUniqueValue(value: string) {
    return this.editableOptions.filter(option => option["value"] === value).length === 1;
  }

  allfieldsHaveValues(option: ISelectOption) {
    return Object.keys(option).every(key => option[key] !== "");
  }

  uniqueColumnNameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const isUniqueName = !this.cols.includes(control.value);
      if (isUniqueName)
        return null;
      return { uniqueName: true };
    }
  }
  //#endregion Validation

  //#region File Upload
  parseExcelHeaderRow(row: ExcelJS.Row, headers: string[]) {
    const headerValues = Object.values(row.values);
    if (!headerValues.includes('value'))
      throw new Error("'value' column is required");
    if (!headerValues.includes('text'))
      throw new Error("'text' column is required");
    // Check if header values are unique
    const uniqueHeaderValues = new Set(headerValues);
    if (headerValues.length !== uniqueHeaderValues.size)
      throw new Error("Header values must be unique");

    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      const value = cell.value as string;
      if (!cell.value)
        throw new Error("All headers need a value")
      if (!this.COLUM_NAME_PATTERN.test(value))
        throw new Error("Headers can't have whitespaces")
      headers[colNumber] = value;
    });
  }

  parseExcelRow(row: ExcelJS.Row, headers: string[], tableData: ISelectOption[], uniqueValueSet: Set<string>) {
    let rowData: ISelectOption = { value: '', text: '' };
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      if (headers[colNumber] === undefined)
        throw new Error("Column header is missing");
      if (!cell.value)
        throw new Error("All row fields need a value");

      if (headers[colNumber] === "text") {
        rowData.text = cell.value as string;
      } else if (headers[colNumber] === "value") {
        //make sure the values are unique
        const cellValue = cell.value as string;
        if (uniqueValueSet.has(cellValue))
          throw new Error(`The 'value' column cannot have duplicates: '${cellValue}'`);
        uniqueValueSet.add(cellValue);
        rowData.value = cell.value as string;
      } else {
        rowData[headers[colNumber]] = cell.value as string;
      }
    });
    tableData.push(rowData);
  }

  parseExcel(arrayBuffer: any): Promise<{ valid: boolean, msg: string, data: ISelectOption[] }> {
    const uniqueValueSet = new Set<string>();
    const workbook = new ExcelJS.Workbook();
    return workbook.xlsx.load(arrayBuffer).then((workbook) => {
      let tableData: ISelectOption[] = [];
      let headers: string[] = ['id'];
      const worksheet = workbook.worksheets[0]; // Access the first worksheet directly
      try {
        worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
          if (rowNumber === 1)
            this.parseExcelHeaderRow(row, headers);
          else
            this.parseExcelRow(row, headers, tableData, uniqueValueSet);
        });
      } catch (error) {
        if (error instanceof Error) {
          return { valid: false, msg: error.message, data: [] };
        }
      }
      return { valid: true, msg: "success", data: tableData };
    });
  }

  uploadHandler(event: FileUploadHandlerEvent, fileUpload: FileUpload) {
    const file = event.files[0];
    const fileReader = new FileReader();
    fileReader.onload = (e: any) => {
      const arrayBuffer = e.target.result;
      this.parseExcel(arrayBuffer).then(args => {
        if (args.valid) {
          this.initializeColumns(args.data);
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Options were imported' });
        } else {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: args.msg });
        }
      });
    };

    fileReader.readAsArrayBuffer(file);
    fileUpload.clear();
  }

  //#endregion File Upload

  print() {
    console.log("options");
    console.log(this.originalOptions);
    console.log("editableOptions");
    console.log(this.editableOptions);
    console.log("cols");
    console.log(this.cols);
    console.log("deletableCols");
    console.log(this.deletableCols);
    console.log("clonedOptions");
    console.log(this.clonedOptions);
  }
}
